import React, { useEffect, useState } from "react";
import { DropDownInput, TextInput } from "../FormBuilder/InputField/InputField.jsx";
import { OnboardingOrgDataSchema } from "../../constant/OnboardingOrganization/OnboardingOrganization";
import { getAlldata, createOrg, updateUserLastOrg } from "../../controllers/APIController";
import { Button, Spinner } from "@nextui-org/react";
import { ProgessColors } from "../../utils/constant";
import { useNavigate } from "react-router-dom";

// Custom form field wrapper for professional styling
const ProfessionalFormField = ({ children, label, required, error }) => (
  <div className="space-y-2">
    <label className="block text-sm font-medium text-neutral-700">
      {label}
      {required && <span className="text-error-500 ml-1">*</span>}
    </label>
    <div className="relative">
      {children}
      {error && <p className="text-error-500 text-xs mt-1">{error}</p>}
    </div>
  </div>
);

const OrgBasicInfo = ({
  DataList,
  setDataList,
  TitleText,
  DescriptionText,
  currentPage,
  setCurrrentPage,
  totalStep,
  allUrlList,
}) => {
  let schemaData = OnboardingOrgDataSchema;

  const navigate = useNavigate();
  const OrgUrlValue = DataList["Name"]?.toLowerCase()?.replaceAll(" ", "-");
  const [allSectorName, setAllSectorName] = useState([]);
  const [validationErrors, setValidationErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [orgCreateStatus, setOrgCreateStatus] = useState(true);

  const UserLogged = JSON.parse(sessionStorage.getItem("userData"));
  const UserId = UserLogged && UserLogged.user.id;

  const ValidationHandler = () => {
    const orgNameRegex = /[^a-zA-Z0-9 _-]/;
    const OrgName = DataList?.Name;
    const OrgUrlValue = OrgName?.toLowerCase()?.replaceAll(" ", "-");
    const err = {};
    let isValid = true;
    setValidationErrors({});

    if (OrgName?.trim() === "") {
      err["Name"] = "Organization name is required!";
      isValid = false;
    }

    if (allUrlList?.includes(OrgUrlValue)) {
      err["URL"] = "This organization already exists!";
      isValid = false;
    }

    if (orgNameRegex.test(OrgName)) {
      err["Name"] =
        "Organization Name don't contain special character except space, _ , - ";
      isValid = false;
    }

    console.log(err);
    setValidationErrors(err);
    return isValid;
  };

  const handleNext = async () => {
    if (ValidationHandler()) {
      if (totalStep === 1) {
        // Create organization directly since this is the only step
        try {
          setIsLoading(true);
          setOrgCreateStatus(true);
          
          // Create organization with minimal required data and defaults
          const orgData = {
            Name: DataList["Name"],
            URL: OrgUrlValue,
            UserId: UserId,
            "Financial Year": new Date().getFullYear().toString(),
            "Choose Your Region": "Not Specified",
            "Choose Industry Sector": "Not Specified",
            "Select Framework": [],
            tags: [],
            "Financial Name": "",
            "Start Date": "",
            "End Date": "",
            // BRSR Fields (empty defaults)
            "Corporate Identity Number": "",
            "Listed Entity Name": "",
            "Year of Incorporation": "",
            "Registered Office Address": "",
            "Corporate Address": "",
            "Email": "",
            "Telephone": "",
            "Website": "",
            "Stock Exchange": "",
            "Paid-up Capital": "",
            "BRSR Contact Person": "",
            "BRSR Contact Phone": "",
            "BRSR Contact Email": "",
            "Reporting Boundary": "",
            "Assurance Provider": "",
            "Assurance Type": "",
          };
          
          const response = await createOrg(orgData);
          const orgId = response?.data?.id;

          if (orgId) {
            // Update user's last organization
            await updateUserLastOrg(UserId, OrgUrlValue, orgId);
            
            // Navigate to the dashboard
            navigate(`/${OrgUrlValue}/dashboard`);
          } else {
            throw new Error("Organization creation failed");
          }
        } catch (error) {
          console.error("Error creating organization:", error);
          setOrgCreateStatus(false);
          setIsLoading(false);
        }
      } else {
        setCurrrentPage((prevPage) => prevPage + 1);
      }
    }
  };
  // Removed sectors fetch since we're only collecting organization name now

  return (
    <>
      <div className="mb-8">
        <h2 className="text-xl font-semibold text-neutral-900 mb-2">
          {TitleText || "Organization Details"}
        </h2>
        <p className="text-neutral-600 text-sm leading-relaxed">
          {DescriptionText ||
            `Provide basic information about your organization to begin your ESG reporting journey.`}
        </p>
      </div>
      <div className="space-y-6">
        <ProfessionalFormField
          label="Organization Name"
          required={schemaData["Name"]?.required}
          error={validationErrors["Name"]}
        >
          <TextInput
            data={{...schemaData["Name"], title: ""}}
            value={DataList["Name"]}
            validationErrors={{}}
            onChangeHandler={(title, value, type) => {
              setDataList({ ...DataList, ["Name"]: value });
            }}
            cssStyle="mb-0"
          />
        </ProfessionalFormField>

        <ProfessionalFormField
          label="Organization URL"
          required={false}
          error={validationErrors["URL"]}
        >
          <TextInput
            data={{...schemaData["URL"], title: ""}}
            value={OrgUrlValue}
            validationErrors={{}}
            onChangeHandler={1}
            cssStyle="mb-0"
          />
        </ProfessionalFormField>

        <div className="mt-8 p-4 bg-emerald-50 rounded-lg border border-emerald-200">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0">
              <lord-icon
                src="https://cdn.lordicon.com/kiynvdns.json"
                trigger="hover"
                
                style={{ width: "24px", height: "24px" }}
              ></lord-icon>
            </div>
            <div>
              <h4 className="text-sm font-semibold text-emerald-900 mb-1">Quick Setup</h4>
              <p className="text-sm text-primary-700">
                You can add detailed organization information (BRSR details, financial data, etc.) later from the Settings page. 
                Let's get your organization created first!
              </p>
            </div>
          </div>
        </div>
      </div>

      {!orgCreateStatus && (
        <div className="mt-6 p-4 bg-red-50 rounded-lg border border-red-200">
          <p className="text-error-700 text-sm">
            Something went wrong while creating the organization. Please try again.
          </p>
        </div>
      )}

      {isLoading && (
        <div className="mt-6 p-4 bg-amber-50 rounded-lg border border-amber-200">
          <div className="flex items-center gap-3">
            <Spinner size="sm" color="warning" />
            <div>
              <p className="text-amber-700 text-sm font-medium">Creating your organization...</p>
              <p className="text-amber-600 text-xs">This may take a moment, please wait.</p>
            </div>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between pt-8 border-t border-neutral-200 mt-10">
        <div className="text-sm text-neutral-500 font-medium">
          {totalStep === 1 ? "Ready to create your organization?" : `Step ${currentPage + 1} of ${totalStep}`}
        </div>
        <div className="flex space-x-3">
          {/* <Button
            variant="ghost"
            size="sm"
            className="text-neutral-600 hover:text-neutral-900 px-4"
            isDisabled={currentPage === 0 || isLoading}
            onClick={() => setCurrrentPage(currentPage - 1)}
          >
            Previous
          </Button> */}
          <Button
            color="primary"
            size="sm"
            className="bg-primary-600 hover:bg-primary-700 text-white px-6 font-medium"
            onClick={handleNext}
            isLoading={isLoading}
            isDisabled={isLoading}
          >
            {totalStep === 1 ? "Create Organization" : "Continue"}
          </Button>
        </div>
      </div>
    </>
  );
};

export default OrgBasicInfo;
